import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';
import {defineMessages, injectIntl} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import intlShape from '../../lib/intlShape.js';

import styles from './domi-examples.css';

const messages = defineMessages({
    title: {
        id: 'gui.domiExamples.title',
        defaultMessage: 'Examples Center',
        description: 'Title of the hardware examples center'
    },
    deviceIntro: {
        id: 'gui.domiExamples.deviceIntro',
        defaultMessage: 'Choose a device to explore ready-to-use classroom projects.',
        description: 'Introductory text on the device selection page'
    },
    chooseDevice: {
        id: 'gui.domiExamples.chooseDevice',
        defaultMessage: 'Choose a device',
        description: 'Heading on the device selection page'
    },
    chooseDeviceHelp: {
        id: 'gui.domiExamples.chooseDeviceHelp',
        defaultMessage: 'Each example automatically loads the required extension and complete blocks.',
        description: 'Help on the device selection page'
    },
    examplesCount: {
        id: 'gui.domiExamples.examplesCount',
        defaultMessage: '{count} examples',
        description: 'Number of examples available for a device'
    },
    comingSoon: {
        id: 'gui.domiExamples.comingSoon',
        defaultMessage: 'Coming soon',
        description: 'Label for a device whose examples are not available yet'
    },
    backToDevices: {
        id: 'gui.domiExamples.backToDevices',
        defaultMessage: 'Back to devices',
        description: 'Button that returns to the device selection page'
    },
    online: {
        id: 'gui.domiExamples.mode.online',
        defaultMessage: 'Live control',
        description: 'Label for a live control example'
    },
    offline: {
        id: 'gui.domiExamples.mode.offline',
        defaultMessage: 'Offline program',
        description: 'Label for an offline example'
    },
    beginner: {
        id: 'gui.domiExamples.beginner',
        defaultMessage: 'Beginner',
        description: 'Difficulty label for beginner examples'
    },
    advanced: {
        id: 'gui.domiExamples.advanced',
        defaultMessage: 'Advanced',
        description: 'Difficulty label for advanced examples'
    },
    preview: {
        id: 'gui.domiExamples.preview',
        defaultMessage: 'Preview effect',
        description: 'Button that replays an example preview'
    },
    programIncludes: {
        id: 'gui.domiExamples.programIncludes',
        defaultMessage: 'This example includes',
        description: 'Heading for the actions in an example project'
    },
    safety: {
        id: 'gui.domiExamples.safety',
        defaultMessage: 'Loading an example will not start the car. Connect the car, then click the green flag ' +
            'or trigger its event.',
        description: 'Safety note in the Domi examples center'
    },
    offlineHelp: {
        id: 'gui.domiExamples.offlineHelp',
        defaultMessage: 'After loading, open Burn Center to review and write this program to the car.',
        description: 'Help shown for an offline Domi car example'
    },
    cancel: {
        id: 'gui.domiExamples.cancel',
        defaultMessage: 'Close',
        description: 'Button to close the examples center'
    },
    useExample: {
        id: 'gui.domiExamples.useExample',
        defaultMessage: 'Use this example',
        description: 'Button to load an example project'
    },
    loading: {
        id: 'gui.domiExamples.loading',
        defaultMessage: 'Loading example…',
        description: 'Button label while an example is loading'
    }
});

const roundPreviewText = {
    'round-hello': 'HELLO!',
    'round-touch': 'TOUCH ME',
    'round-reaction': 'TAP!',
    'round-controller': '↖  ↗\n↙  ↘',
    'round-badge': 'DOMI\nMAKER',
    'round-paint': '●  ●\n  ●  ●',
    'round-target': '◎\nSCORE 3',
    'round-pet': '•  •\n ^_^',
    'round-dice': '⚄\nSHAKE!',
    'round-orbit': '   ●\nTILT',
    'round-rhythm': '♪  ●  ♪\nPERFECT!',
    'round-space': '✦  ▲  ●\nDODGE!',
    'round-pet-adventure': '♥  LV.7\nDANCE!',
    'round-battery': '73%\nPOWER'
};

const PreviewScene = ({deviceIcon, type, replayKey}) => (
    <div
        key={`${type}-${replayKey}`}
        className={classNames(styles.previewScene, styles[`${type}Preview`])}
    >
        <div className={styles.previewGrid} />
        <div className={styles.routeLine} />
        <div className={styles.cameraFrame}>
            <span className={styles.cameraLens}>●</span>
            <span className={styles.scanLine} />
        </div>
        <div className={styles.lightGlow} />
        <div className={styles.roundScreen}>
            <span>{roundPreviewText[type] || ''}</span>
            <i />
        </div>
        <img
            alt=""
            className={styles.previewCar}
            src={deviceIcon}
        />
        <div className={styles.keypad}>
            <span>↑</span>
            <span>←</span><span>↓</span><span>→</span>
        </div>
        <div className={styles.offlineBadge}>OFFLINE</div>
    </div>
);

PreviewScene.propTypes = {
    deviceIcon: PropTypes.string.isRequired,
    replayKey: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired
};

const DeviceCard = ({device, intl, onSelect}) => {
    const handleSelect = useCallback(() => onSelect(device.id), [device.id, onSelect]);
    return (
        <button
            className={classNames(styles.deviceCard, {
                [styles.unavailableDevice]: !device.available
            })}
            disabled={!device.available}
            type="button"
            onClick={handleSelect}
        >
            <span className={styles.deviceIcon}>
                <img
                    alt=""
                    src={device.icon}
                />
            </span>
            <span className={styles.deviceContent}>
                <strong>{intl.formatMessage(device.title)}</strong>
                <span>{intl.formatMessage(device.description)}</span>
                <small>{device.available ? intl.formatMessage(messages.examplesCount, {
                    count: device.examples.length
                }) : intl.formatMessage(messages.comingSoon)}</small>
            </span>
            <span className={styles.deviceArrow}>{device.available ? '›' : '···'}</span>
        </button>
    );
};

const ExampleCard = ({deviceIcon, example, intl, isSelected, onSelect}) => {
    const handleSelect = useCallback(() => onSelect(example.id), [example.id, onSelect]);
    return (
        <button
            className={classNames(styles.exampleCard, {
                [styles.selectedCard]: isSelected
            })}
            type="button"
            onClick={handleSelect}
        >
            <span className={styles.cardIcon}>
                <img
                    alt=""
                    src={deviceIcon}
                />
            </span>
            <span className={styles.cardText}>
                <strong>{intl.formatMessage(example.title)}</strong>
                <small>{intl.formatMessage(
                    example.mode === 'offline' ? messages.offline : messages.online
                )}</small>
            </span>
            <span className={styles.cardArrow}>›</span>
        </button>
    );
};

const messageShape = PropTypes.shape({
    defaultMessage: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired
});

const exampleShape = PropTypes.shape({
    actions: PropTypes.arrayOf(messageShape).isRequired,
    description: messageShape.isRequired,
    id: PropTypes.string.isRequired,
    level: PropTypes.oneOf(['beginner', 'advanced']),
    mode: PropTypes.oneOf(['online', 'offline']).isRequired,
    preview: PropTypes.string.isRequired,
    projectPath: PropTypes.string.isRequired,
    title: messageShape.isRequired
});

const deviceShape = PropTypes.shape({
    available: PropTypes.bool.isRequired,
    description: messageShape.isRequired,
    examples: PropTypes.arrayOf(exampleShape).isRequired,
    icon: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    help: messageShape,
    title: messageShape.isRequired
});

DeviceCard.propTypes = {
    device: deviceShape.isRequired,
    intl: intlShape.isRequired,
    onSelect: PropTypes.func.isRequired
};

ExampleCard.propTypes = {
    deviceIcon: PropTypes.string.isRequired,
    example: exampleShape.isRequired,
    intl: intlShape.isRequired,
    isSelected: PropTypes.bool.isRequired,
    onSelect: PropTypes.func.isRequired
};

const DomiExamples = ({devices, error, intl, loadingId, onLoadExample, onRequestClose}) => {
    const [selectedDeviceId, setSelectedDeviceId] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [replayKey, setReplayKey] = useState(0);
    const selectedDevice = devices.find(device => device.id === selectedDeviceId);
    const examples = selectedDevice ? selectedDevice.examples : [];
    const selected = examples.find(example => example.id === selectedId) || examples[0];

    const handleSelectDevice = useCallback(deviceId => {
        const device = devices.find(item => item.id === deviceId);
        if (!device || !device.available) return;
        setSelectedDeviceId(device.id);
        setSelectedId(device.examples[0] ? device.examples[0].id : null);
    }, [devices]);

    const handleBackToDevices = useCallback(() => {
        setSelectedDeviceId(null);
        setSelectedId(null);
    }, []);

    useEffect(() => {
        setReplayKey(previous => previous + 1);
    }, [selectedId]);

    const handleReplay = useCallback(() => {
        setReplayKey(previous => previous + 1);
    }, []);

    const handleUseSelected = useCallback(() => {
        if (selected) onLoadExample(selected);
    }, [onLoadExample, selected]);

    return (
        <Modal
            className={styles.modal}
            contentLabel={intl.formatMessage(messages.title)}
            id="domi-examples"
            onRequestClose={onRequestClose}
        >
            <div className={styles.body}>
                {selectedDevice ? (
                    <div className={styles.subpageHeader}>
                        <button
                            className={styles.backButton}
                            type="button"
                            onClick={handleBackToDevices}
                        >
                            ‹ {intl.formatMessage(messages.backToDevices)}
                        </button>
                        <span className={styles.breadcrumbSeparator}>/</span>
                        <strong>{intl.formatMessage(selectedDevice.title)}</strong>
                    </div>
                ) : (
                    <div className={styles.intro}>{intl.formatMessage(messages.deviceIntro)}</div>
                )}

                {selectedDevice ? (
                    <div className={styles.workspace}>
                        <div className={styles.exampleList}>
                            {examples.map(example => (
                                <ExampleCard
                                    deviceIcon={selectedDevice.icon}
                                    example={example}
                                    intl={intl}
                                    isSelected={Boolean(selected && selected.id === example.id)}
                                    key={example.id}
                                    onSelect={setSelectedId}
                                />
                            ))}
                        </div>

                        {selected ? (
                            <div className={styles.detail}>
                                <div className={styles.detailHeader}>
                                    <div>
                                        <div className={styles.tags}>
                                            <span
                                                className={selected.mode === 'offline' ?
                                                    styles.offlineTag : styles.onlineTag}
                                            >
                                                {intl.formatMessage(
                                                    selected.mode === 'offline' ? messages.offline : messages.online
                                                )}
                                            </span>
                                            <span className={styles.levelTag}>
                                                {intl.formatMessage(
                                                    selected.level === 'advanced' ?
                                                        messages.advanced : messages.beginner
                                                )}
                                            </span>
                                        </div>
                                        <h2>{intl.formatMessage(selected.title)}</h2>
                                        <p>{intl.formatMessage(selected.description)}</p>
                                    </div>
                                </div>

                                <PreviewScene
                                    deviceIcon={selectedDevice.icon}
                                    replayKey={replayKey}
                                    type={selected.preview}
                                />

                                <button
                                    className={styles.previewButton}
                                    type="button"
                                    onClick={handleReplay}
                                >
                                    ▶ {intl.formatMessage(messages.preview)}
                                </button>

                                <div className={styles.programSummary}>
                                    <h3>{intl.formatMessage(messages.programIncludes)}</h3>
                                    <ol>
                                        {selected.actions.map(action => (
                                            <li key={action.id}>{intl.formatMessage(action)}</li>
                                        ))}
                                    </ol>
                                </div>

                                {selected.mode === 'offline' ? (
                                    <div className={styles.offlineHelp}>
                                        {intl.formatMessage(messages.offlineHelp)}
                                    </div>
                                ) : null}
                                <div className={styles.safety}>{intl.formatMessage(
                                    selectedDevice.help || messages.safety
                                )}</div>
                                {error ? (
                                    <div
                                        className={styles.error}
                                        role="alert"
                                    >
                                        {error}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <div className={styles.devicePage}>
                        <div className={styles.devicePageHeader}>
                            <h2>{intl.formatMessage(messages.chooseDevice)}</h2>
                            <p>{intl.formatMessage(messages.chooseDeviceHelp)}</p>
                        </div>
                        <div className={styles.deviceGrid}>
                            {devices.map(device => (
                                <DeviceCard
                                    device={device}
                                    intl={intl}
                                    key={device.id}
                                    onSelect={handleSelectDevice}
                                />
                            ))}
                        </div>
                    </div>
                )}
                <div className={styles.footer}>
                    {selectedDevice ? (
                        <button
                            className={styles.backFooterButton}
                            disabled={Boolean(loadingId)}
                            type="button"
                            onClick={handleBackToDevices}
                        >
                            {intl.formatMessage(messages.backToDevices)}
                        </button>
                    ) : null}
                    <button
                        className={styles.cancelButton}
                        disabled={Boolean(loadingId)}
                        type="button"
                        onClick={onRequestClose}
                    >
                        {intl.formatMessage(messages.cancel)}
                    </button>
                    {selectedDevice ? (
                        <button
                            className={styles.useButton}
                            disabled={!selected || Boolean(loadingId)}
                            type="button"
                            onClick={handleUseSelected}
                        >
                            {loadingId ? intl.formatMessage(messages.loading) :
                                intl.formatMessage(messages.useExample)}
                        </button>
                    ) : null}
                </div>
            </div>
        </Modal>
    );
};

DomiExamples.propTypes = {
    error: PropTypes.string,
    devices: PropTypes.arrayOf(deviceShape).isRequired,
    intl: intlShape.isRequired,
    loadingId: PropTypes.string,
    onLoadExample: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired
};

export {DomiExamples};

export default injectIntl(DomiExamples);
